import { type SurfaceTone, toneText } from '@workspace/ui/lib/tone'
import { cn } from '@workspace/ui/lib/utils'
import type { ReactNode } from 'react'

export interface NotePanelProps {
  /** Small uppercase label, e.g. "Why this matters". */
  label: string
  children: ReactNode
  /** `alert` turns the label red, for cards explaining something that broke. */
  tone?: SurfaceTone
  className?: string
}

/**
 * A labelled aside: the reasoning under a card's main content.
 *
 * The Home wizard used to render this text as the card's lede — the sentence
 * directly under the headline. That put the explanation before the thing being
 * explained, and gave the same visual weight to "here is what to do" and "here
 * is why it is worth doing". Boxing it and labelling it makes it skippable,
 * which is the point: a founder who already knows why should be able to jump
 * straight to the action.
 */
export function NotePanel({ label, children, tone = 'default', className }: NotePanelProps) {
  return (
    <div className={cn('rounded-xl border border-border/40 bg-card/30 px-5 py-4', className)}>
      <div
        className={cn(
          'mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em]',
          toneText(tone)
        )}
      >
        {label}
      </div>
      <div className="text-[14px] leading-relaxed text-foreground/80">{children}</div>
    </div>
  )
}
